/**
 * Bootstrap State Utilities
 *
 * Functions to manage the bootstrap input's closed state in localStorage.
 * Separate from the main widget store persistence to keep bootstrap.ts entrypoint small.
 */

import { logger } from '@/lib/logger';
import { getStorageKey } from '@/stores/persistedStorage';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Bootstrap state storage - separate from main widget store
 * Used to persist whether the bootstrap input was closed
 */
interface BootstrapState {
  closed: boolean;
  updatedAt: number;
}

function getBootstrapStorageKey(): string {
  const storageKey = getStorageKey();
  return `${storageKey}-bootstrap-state`;
}

export function getBootstrapClosedState(): boolean {
  try {
    const key = getBootstrapStorageKey();
    const stored = localStorage.getItem(key);
    if (!stored) {
      return false;
    }

    const parsed: BootstrapState = JSON.parse(stored);

    // Check if data is expired (1 day)
    if (parsed.updatedAt && Date.now() - parsed.updatedAt > ONE_DAY_MS) {
      localStorage.removeItem(key);
      return false;
    }

    return parsed.closed ?? false;
  } catch {
    return false;
  }
}

export function setBootstrapClosedState(closed: boolean): void {
  try {
    const key = getBootstrapStorageKey();
    const state: BootstrapState = {
      closed,
      updatedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(state));
    // Dispatch custom event so React components can listen for changes
    window.dispatchEvent(new CustomEvent('bootstrapStateChanged', { detail: { closed } }));
  } catch (error) {
    logger.error('Failed to persist bootstrap state:', error);
  }
}
